import React, { useState, useMemo, useEffect } from 'react';
import { useGrid } from '../hooks/useGrid';
import { useAuth } from '../context/AuthContext';
import { useTasks } from '../hooks/useTasks';
import type { TaskOption } from '../hooks/useTasks';
import type { GridRow } from '@spirtech/shared';
import type { PendingSlot } from '../store/gridStore';
import s from './MobileTimesheet.module.css';

const HPD = 8; // 8 hour slots per workday — same as the grid

/*
   Format a Date as YYYY-MM-DD using LOCAL date components.
   toISOString() converts to UTC and shifts the date by one day for users
   east of UTC when the Date is at local midnight, causing slot lookups
   to miss the intended day.
*/
function isoDate(d: Date): string {
	const vY = d.getFullYear(); // local year
	const vM = String(d.getMonth() + 1).padStart(2, '0'); // local month, 1-based
	const vD = String(d.getDate()).padStart(2, '0'); // local day-of-month
	return `${vY}-${vM}-${vD}`;
}

function addDays(base: Date, n: number): Date {
	const r = new Date(base);
	r.setDate(r.getDate() + n);
	return r;
}

function todayMidnight(): Date {
	const d = new Date();
	d.setHours(0, 0, 0, 0);
	return d;
}

interface Props {
	currentUserId: number;
}

/* Mobile timesheet — one day at a time. Tasks listed down the left; for each
   task, eight hour-cells to the right. Tapping a cell toggles it for that
   task: empty → filled → empty. Tapping a cell that's empty for one task
   while another task is filled at the same hour produces a conflict (red on
   both rows), matching the desktop grid. The hour columns scroll
   horizontally on phones narrower than ~ 460 px; the task column is
   sticky-left so its label stays visible while the user reaches column 8.

   Reuses the same /api/grid + commit pipeline as the desktop, so undo/redo,
   conflict detection, and the past-edit lock all keep working — they're
   driven by the shared Zustand store. */
export function MobileTimesheet({ currentUserId }: Props) {
	const { user } = useAuth();
	const { rows, isFilled, isConflict, commit, loading, error, reload } = useGrid(currentUserId);
	const { createTask, fetchTasks, fetchProjects } = useTasks();

	const [date, setDate] = useState<Date>(todayMidnight);

	// Add-task form — same flow as the desktop grid: choose a project, type
	// (or pick) a task name, submit. After a successful create the grid is
	// reloaded so the new row shows up under the right project.
	const [allProjects, setAllProjects] = useState<{ id: number; name: string; client: string | null }[]>([]); // projects the user can add tasks to
	const [projectsLoaded, setProjectsLoaded] = useState(false); // true once /tasks/my-projects has resolved/failed — distinguishes "still loading" from "no projects assigned"
	const [atProjectId, setAtProjectId] = useState(''); // selected project id (string for <select> value)
	const [atTasks, setAtTasks] = useState<TaskOption[]>([]); // existing tasks on the chosen project, used as datalist suggestions
	const [atName, setAtName] = useState(''); // task name input
	const [atError, setAtError] = useState<string | null>(null); // last create error, cleared on edit
	const [atSaving, setAtSaving] = useState(false); // true while POST /tasks is in flight

	useEffect(() => {
		fetchProjects()
			.then((inPs) => {
				setAllProjects(
					[...inPs].sort((inA, inB) => {
						const vA = inA.client ? `${inA.client} - ${inA.name}` : inA.name;
						const vB = inB.client ? `${inB.client} - ${inB.name}` : inB.name;
						return vA.localeCompare(vB, 'fr');
					}),
				);
			})
			.catch(() => {})
			.finally(() => setProjectsLoaded(true));
	}, []);

	async function handleAtProjectChange(inPid: string) {
		setAtProjectId(inPid);
		setAtName('');
		setAtTasks([]);
		if (!inPid) return;
		try {
			setAtTasks(await fetchTasks(Number(inPid)));
		} catch {
			/* suggestions are optional */
		}
	}

	async function handleAddTask(inEvent: React.FormEvent) {
		inEvent.preventDefault();
		if (!atProjectId || !atName.trim()) return;
		setAtSaving(true);
		setAtError(null);
		try {
			await createTask({ projectId: Number(atProjectId), name: atName.trim() });
			setAtName('');
			setAtProjectId('');
			setAtTasks([]);
			await reload();
		} catch (inErr: any) {
			setAtError(inErr.message);
		} finally {
			setAtSaving(false);
		}
	}

	const today = todayMidnight();
	const dateStr = isoDate(date);
	const isToday = date.getTime() === today.getTime();
	const dow = date.getDay();
	const isWE = dow === 0 || dow === 6;

	// Mobile shows only the current user's rows — managers don't get a multi-
	// user view here, the screen is too narrow to be useful.
	const myRows: GridRow[] = useMemo(() => rows.filter((r) => r.userId === currentUserId), [rows, currentUserId]);

	// Toggle (row, hour). Single-cell change → one batch entry.
	function toggleCell(row: GridRow, hour: number) {
		const filled = isFilled(row.userId, row.taskId, dateStr, hour);
		const pending: PendingSlot[] = [
			{
				userId: row.userId,
				taskId: row.taskId,
				entryDate: dateStr,
				hourSlot: hour,
				filled: !filled,
			},
		];
		void commit(pending);
	}

	return (
		<div className={s.page}>
			{/* Date navigation */}
			<div className={s.header}>
				<button className={s.navBtn} onClick={() => setDate((d) => addDays(d, -1))}>
					◀
				</button>
				<span className={`${s.dateLabel} ${isWE ? s.weekend : ''}`}>
					{date.toLocaleDateString('fr-FR', {
						weekday: 'short',
						day: 'numeric',
						month: 'short',
						year: 'numeric',
					})}
				</span>
				<button className={s.navBtn} onClick={() => setDate((d) => addDays(d, +1))}>
					▶
				</button>
			</div>
			<div className={s.todayRow}>
				<button className={`${s.todayBtn} ${isToday ? s.active : ''}`} onClick={() => setDate(todayMidnight())}>
					Aujourd'hui
				</button>
			</div>

			{/* Add-task form — collapses to two stacked rows on narrow phones so
			    the inputs stay tappable down to ~ 320 px viewports. */}
			<form className={s.addTaskForm} onSubmit={handleAddTask}>
				<select
					required
					className={s.addTaskSelect}
					value={atProjectId}
					onChange={(inEvent) => handleAtProjectChange(inEvent.target.value)}
				>
					<option value="">Projet…</option>
					{allProjects.map((inP) => (
						<option key={inP.id} value={inP.id}>
							{inP.client ? `${inP.client} - ${inP.name}` : inP.name}
						</option>
					))}
				</select>
				<div className={s.addTaskRow}>
					<input
						required
						className={s.addTaskInput}
						placeholder={atProjectId ? 'Nom de la tâche…' : "Choisir un projet d'abord"}
						value={atName}
						onChange={(inEvent) => {
							setAtName(inEvent.target.value);
							setAtError(null);
						}}
						disabled={!atProjectId}
						list="mobile-task-suggestions"
						autoComplete="off"
					/>
					<datalist id="mobile-task-suggestions">
						{atTasks
							.filter((inT) => inT.isActive)
							.map((inT) => (
								<option key={inT.id} value={inT.name} />
							))}
					</datalist>
					<button
						type="submit"
						className={s.addTaskBtn}
						disabled={atSaving || !atProjectId || !atName.trim()}
					>
						{atSaving ? '…' : 'Ajouter'}
					</button>
				</div>
				{atError && <div className={s.addTaskError}>{atError}</div>}
			</form>

			{error && <div className={s.error}>Erreur : {error}</div>}
			{loading && <div className={s.msg}>Chargement…</div>}
			{!loading && myRows.length === 0 && (
				<div className={s.msg}>
					{projectsLoaded && allProjects.length === 0
						? 'Aucun projet assigné. Demandez à votre responsable de vous ajouter à un projet.'
						: 'Aucune tâche à afficher. Utilisez le formulaire ci-dessus pour ajouter votre première tâche.'}
				</div>
			)}

			{!loading && myRows.length > 0 && (
				<div className={s.taskGrid}>
					{myRows.map((row) => (
						<div key={`${row.taskId}`} className={s.taskRow}>
							<div className={s.taskCol}>
								<div className={s.taskName}>{row.taskName}</div>
								<div className={s.projName}>
									{row.projectClient ? `${row.projectClient} – ${row.projectName}` : row.projectName}
								</div>
							</div>
							<div className={s.hourCells}>
								{Array.from({ length: HPD }, (_, h) => {
									const filled = isFilled(row.userId, row.taskId, dateStr, h);
									const conflict = isConflict(row.userId, row.taskId, dateStr, h);
									const cls = conflict ? s.cellConflict : filled ? s.cellFilled : s.cell;
									return (
										<button
											key={h}
											type="button"
											className={`${s.cell} ${cls}`}
											aria-label={`Heure ${h + 1}`}
											onClick={() => toggleCell(row, h)}
										>
											{h + 1}
										</button>
									);
								})}
							</div>
						</div>
					))}
				</div>
			)}

			{/* Compact who-am-I footer — useful when a manager triggers mobile mode */}
			{user && (
				<div className={s.msg} style={{ padding: 4 }}>
					{user.fullName} · {user.initials}
				</div>
			)}
		</div>
	);
}
